import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Stack,
  Alert,
  CircularProgress,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import API from "../../api/axiosConfig";

/**
 * ReimbursementForm Component
 * Handles uploading new reimbursements and viewing existing ones.
 */
const ReimbursementForm = () => {
  // --- Form State ---
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  // should reimbursement be paid to self?
  const [isPaidToSelf, setIsPaidToSelf] = useState(true); // assume user is the recipient unless otherwise stated
  // who is the recipient of the reimbursement
  const [recipientName, setrecipientName] = useState("");
  const [recipientEmail, setrecipientEmail] = useState("");

  const [error, setError] = useState("");

  // --- UI/UX State ---
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Handle File Selection
  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  /**
   * Submit the reimbursement (POST /api/reimbursements/upload)
   */
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !amount || !reason) {
      setMessage({
        type: "error",
        text: "Please provide an amount, reason, and receipt image.",
      });
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });

    // Interceptor handles the token, so we don't need to 'getItem' here.
    const formData = new FormData();
    formData.append("receipt", selectedFile);
    formData.append("amount", amount);
    formData.append("reason", reason || null);
    formData.append("paidToSelf", isPaidToSelf);
    if(!isPaidToSelf){
        formData.append("paidToEmail", recipientEmail);
        formData.append("paidToName", recipientName);
    }

    try {
      const response = await API.post("/api/reimbursements/upload", formData);

      setMessage({
        type: "success",
        text: "Reimbursement successfully submitted with id: " + response.data,
      });
      setAmount("");
      setReason("");
      setSelectedFile(null);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "An error occurred during upload.";
      setMessage({ type: "error", text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: 3 }}>
      <Typography
        variant="h4"
        gutterBottom
        align="center"
        sx={{ fontWeight: "bold" }}
      >
        Reimbursement Portal
      </Typography>

      {message.text && (
        <Alert
          severity={message.type === "success" ? "success" : "error"}
          sx={{ mb: 3 }}
        >
          {message.text}
        </Alert>
      )}

      {/* Submission Card */}
      <Card elevation={3} sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Submit New Request
          </Typography>
          <form onSubmit={handleUpload}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MoneyIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <Box sx={{ width: "100%", mb: 3, textAlign: "left" }}>
                <Typography
                  variant="body2"
                  sx={(theme) => ({
                    fontWeight: "bold",
                    mb: 1,
                    color: theme.palette.paper.light,
                  })}
                >
                  Reimbursement recipient
                </Typography>
                  <Stack spacing={3} sx={{ mt: 2 }}>
                    <ToggleButtonGroup
                      value={isPaidToSelf}
                      exclusive
                      onChange={(event, newValue) => {
                        if (newValue !== null) {
                          setIsPaidToSelf(newValue);
                          setError("");
                        }
                      }}
                      fullWidth
                      sx={{
                        height: "40px",
                        "& .MuiToggleButton-root": {
                          textTransform: "none",
                          fontWeight: "medium",
                          color: "grey.500",
                          borderColor: "rgba(255, 255, 255, 0.2)",
                          "&.Mui-selected": {
                            color: "#90caf9", 
                            backgroundColor: "rgba(144, 202, 249, 0.08)",
                            borderColor: "#90caf9",
                            "&:hover": {
                              backgroundColor: "rgba(144, 202, 249, 0.15)",
                            },
                          },
                        },
                      }}
                    >
                      <ToggleButton value={true}>
                        Assign to self
                      </ToggleButton>
                      <ToggleButton value={false}>
                        Assign to someone else
                      </ToggleButton>
                    </ToggleButtonGroup>

                    {!isPaidToSelf && (
                      <Stack spacing={3} sx={{ mt: 2 }}>
                        <TextField
                          fullWidth
                          label="recipient Name"
                          value={recipientName}
                          onChange={(e) => setrecipientName(e.target.value)}
                        />
 
                        <TextField
                          fullWidth
                          label="recipient Email"
                          value={recipientEmail}
                          onChange={(e) => setrecipientEmail(e.target.value)}
                          helperText={error}
                          FormHelperTextProps={{
                            sx: {
                              position: "absolute", 
                              bottom: "-18px", 
                              marginLeft: 0,
                              color: "#ff1744",
                            },
                          }}
                        />
                      </Stack>
                    )}
                  </Stack>
              </Box>

              <Box
                sx={{
                  border: "1px dashed grey",
                  p: 2,
                  textAlign: "center",
                  borderRadius: 1,
                }}
              >
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="receipt-file-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="receipt-file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                  >
                    Choose Receipt Image
                  </Button>
                </label>
                {selectedFile && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: "text.secondary" }}
                  >
                    Selected: {selectedFile.name}
                  </Typography>
                )}
              </Box>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                startIcon={
                  loading ? <CircularProgress size={20} /> : <ReceiptIcon />
                }
              >
                {loading ? "Processing..." : "Submit Claim"}
              </Button>
              <Button variant="outlined" onClick={() => window.history.back()}>
                Back
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ReimbursementForm;
